import {
  SystemSetting,
  CreateSettingRequest,
  SettingHistoryResponse,
  SettingCategory,
} from '../types/systemSettings';
import { get, post, put, del } from '../utils/httpClient';

const BASE_PATH = '/api/v1/admin/settings';

async function parseErrorMessage(response: Response, fallback: string): Promise<string> {
  try {
    const data = await response.json();
    return data.error || data.message || fallback;
  } catch {
    return fallback;
  }
}

/** Fetch all settings grouped by category */
export async function fetchAllSettings(): Promise<Record<string, SystemSetting[]>> {
  const response = await get(BASE_PATH);
  if (!response.ok) {
    const msg = await parseErrorMessage(response, 'Erreur lors du chargement des paramètres');
    throw new Error(msg);
  }
  return response.json();
}

/** Fetch available categories */
export async function fetchCategories(): Promise<string[]> {
  const response = await get(`${BASE_PATH}/categories`);
  if (!response.ok) {
    const msg = await parseErrorMessage(response, 'Erreur lors du chargement des catégories');
    throw new Error(msg);
  }
  const data = await response.json();
  return data.categories || [];
}

/** Fetch settings for a specific category */
export async function fetchSettingsByCategory(category: SettingCategory): Promise<SystemSetting[]> {
  const response = await get(`${BASE_PATH}/category/${category}`);
  if (!response.ok) {
    const msg = await parseErrorMessage(response, 'Erreur lors du chargement des paramètres');
    throw new Error(msg);
  }
  return response.json();
}

/** Fetch a single setting by key */
export async function fetchSettingByKey(key: string): Promise<SystemSetting> {
  const response = await get(`${BASE_PATH}/${key}`);
  if (!response.ok) {
    const msg = await parseErrorMessage(response, 'Erreur lors du chargement du paramètre');
    throw new Error(msg);
  }
  return response.json();
}

/** Create a new setting */
export async function createSetting(data: CreateSettingRequest): Promise<SystemSetting> {
  const response = await post(BASE_PATH, data);
  if (!response.ok) {
    const msg = await parseErrorMessage(response, 'Erreur lors de la création du paramètre');
    throw new Error(msg);
  }
  return response.json();
}

/** Update a setting value */
export async function updateSetting(key: string, value: string): Promise<SystemSetting> {
  const response = await put(`${BASE_PATH}/${key}`, { value });
  if (!response.ok) {
    const msg = await parseErrorMessage(response, 'Erreur lors de la mise à jour du paramètre');
    throw new Error(msg);
  }
  return response.json();
}

/** Bulk update multiple settings */
export async function bulkUpdateSettings(
  settings: Record<string, string>
): Promise<{ message: string; settings: SystemSetting[] }> {
  const response = await put(BASE_PATH, { settings });
  if (!response.ok) {
    const msg = await parseErrorMessage(response, 'Erreur lors de la mise à jour groupée');
    throw new Error(msg);
  }
  return response.json();
}

/** Delete a setting */
export async function deleteSetting(key: string): Promise<void> {
  const response = await del(`${BASE_PATH}/${key}`);
  if (!response.ok) {
    const msg = await parseErrorMessage(response, 'Erreur lors de la suppression du paramètre');
    throw new Error(msg);
  }
}

/** Fetch change history for a setting */
export async function fetchSettingHistory(
  key: string,
  limit = 50
): Promise<SettingHistoryResponse> {
  const response = await get(`${BASE_PATH}/${key}/history?limit=${limit}`);
  if (!response.ok) {
    const msg = await parseErrorMessage(response, "Erreur lors du chargement de l'historique");
    throw new Error(msg);
  }
  return response.json();
}

/** Export all settings as JSON (client-side) */
export function exportSettingsAsJson(settings: Record<string, SystemSetting[]>): void {
  const blob = new Blob([JSON.stringify(settings, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `system-settings-${new Date().toISOString().split('T')[0]}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

/** Import settings from JSON file (returns parsed settings for bulk update) */
export async function parseImportFile(file: File): Promise<Record<string, string>> {
  const text = await file.text();
  const parsed = JSON.parse(text);

  // Flatten category-grouped settings into key-value pairs
  const flat: Record<string, string> = {};
  if (typeof parsed === 'object' && parsed !== null) {
    for (const category of Object.values(parsed)) {
      if (Array.isArray(category)) {
        for (const setting of category) {
          if (setting.key && setting.value !== undefined) {
            flat[setting.key] = String(setting.value);
          }
        }
      }
    }
  }
  if (Object.keys(flat).length === 0) {
    throw new Error('Aucun paramètre valide trouvé dans le fichier');
  }
  return flat;
}
